/**
 * @file src/views/VID/views/hooks/sessionHooks.ts
 * @exports useSessionValue, useSessionSet, useSession, useSessionFileAtom
 * @summary Hooks for managing localStorage state prefixed with vidSessionId for vID flow with TTL cleanup
 * @remarks These hooks manage localStorage state prefixed with vidSessionId. They are used the same as
 *          useAtom, useAtomValue, and useSetAtom from jotai, except they also accept an optional vidSessionId.
 *          All state is stored under one vidSessionId for easier management and cross-tab compatibility.
 *          There is no point using these hooks for atoms that do not persist via local storage.
 *          A storage key must come from the URL (`vidSessionId` / `voucherId`) or session storage; there is no fallback key.
 *          On routes where {@link isVidPathExemptFromPartitionGuard} is true (landing, invite entry, remote-capture init), a missing key returns
 *          defaults instead of throwing so `useFetchData` and other hooks can run before a partition exists (matches `VidSessionKeyGuard`).
 * @see `src/views/VID/vidStorageKey.ts` for partition key resolution (`resolveAndPersistVidPartitionKey`).
 */

import { useAtomValue, useSetAtom } from "jotai";
import { useLocation, useSearch } from "@tanstack/react-router";
import { focusAtom } from "jotai-optics";
import { useCallback, useEffect, useMemo } from "react";
import {
	atomWithAsyncFileStorage,
	clearIndexedDBForSession,
	dataStoreAtom,
	didRunVidSessionCleanupAtom,
	defaultVIDStateValues,
	VIDState,
} from "src/shame/shame-atoms";
import { RESET } from "jotai/utils";

import { isVidPathExemptFromPartitionGuard, resolveAndPersistVidPartitionKey } from "src/views/VID/vidStorageKey";

const VID_SESSION_UPDATED_AT_KEY = "vidSessionUpdatedAt";

const noopVidSessionSetFn = () => {};

function noopVidSessionSet<K extends keyof VIDState>(): (
	value: VIDState[K] | ((prev: VIDState[K]) => VIDState[K]) | typeof RESET,
) => void {
	return noopVidSessionSetFn;
}

const noopSessionFileSet: (value: File | typeof RESET) => void = () => {};

// vID cache TTL is 10 days so users can return to a session later.
const VID_SESSION_TTL_MS = 10 * 24 * 60 * 60 * 1000;

// Use a jotai atom to guard one-time cleanup across concurrent renders

type VidSessionUpdatedAtMap = Record<string, number>;
type DataStore = Record<string, VIDState>;
type SetDataStore = (update: (prev: DataStore) => DataStore) => void;

/** `dataStore` slice used only until the client router is ready (SSR / first paint). */
const PENDING_STORAGE_KEY_RESOLUTION = "__pending_vid_storage_key_resolution__";

type VidStoragePartitionResolution = { phase: "missing" } | { phase: "pending" } | { phase: "ok"; partitionId: string };

const useVidStoragePartitionResolution = (): VidStoragePartitionResolution => {
	const location = useLocation();
	const search = useSearch({ strict: false }) as Record<string, string | string[] | undefined>;
	const canResolve = typeof window !== "undefined" && true;

	// Recompute every render so tab-session writes made immediately before navigation are visible.
	// that happen on the same navigation tick as invite processing.
	if (!canResolve) {
		return { phase: "pending" };
	}
	const id = resolveAndPersistVidPartitionKey(search as Record<string, unknown>, location.pathname);
	if (id === null) {
		return { phase: "missing" };
	}
	return { phase: "ok", partitionId: id };
};

const throwMissingVidStorageKey = (): never => {
	throw new Error(
		"Missing vID session storage key. Ensure VidSessionKeyGuard wraps this route or the URL includes voucherId or vidSessionId.",
	);
};

const readVidSessionUpdatedAtMap = (): VidSessionUpdatedAtMap => {
	if (typeof window === "undefined") {
		return {};
	}
	const raw = window.localStorage.getItem(VID_SESSION_UPDATED_AT_KEY);
	if (!raw) {
		return {};
	}
	try {
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") {
			return {};
		}
		const entries = Object.entries(parsed as Record<string, unknown>);
		return entries.reduce<VidSessionUpdatedAtMap>((acc, [key, value]) => {
			if (typeof value === "number") {
				acc[key] = value;
			}
			return acc;
		}, {});
	} catch {
		return {};
	}
};

const writeVidSessionUpdatedAtMap = (map: VidSessionUpdatedAtMap) => {
	if (typeof window === "undefined") {
		return;
	}
	window.localStorage.setItem(VID_SESSION_UPDATED_AT_KEY, JSON.stringify(map));
};

const touchVidSessionUpdatedAt = (vidSessionId: string) => {
	if (typeof window === "undefined") {
		return;
	}
	const map = readVidSessionUpdatedAtMap();
	map[vidSessionId] = Date.now();
	writeVidSessionUpdatedAtMap(map);
};

const purgeExpiredVidSessions = (setDataStore: SetDataStore) => {
	const map = readVidSessionUpdatedAtMap();
	const now = Date.now();
	const expiredSessionIds = Object.entries(map)
		.filter(([, updatedAt]) => now - updatedAt >= VID_SESSION_TTL_MS)
		.map(([sessionId]) => sessionId);

	if (expiredSessionIds.length === 0) {
		return;
	}

	setDataStore((prev) => {
		let next = prev;
		let changed = false;
		for (const sessionId of expiredSessionIds) {
			if (sessionId in next) {
				const { [sessionId]: _, ...rest } = next;
				next = rest;
				changed = true;
			}
		}
		return changed ? next : prev;
	});

	for (const sessionId of expiredSessionIds) {
		delete map[sessionId];
		void clearIndexedDBForSession(sessionId);
	}
	writeVidSessionUpdatedAtMap(map);
};

const useVidSessionCacheTtl = (vidSessionId: string) => {
	const setDataStore = useSetAtom(dataStoreAtom);
	const didRunCleanup = useAtomValue(didRunVidSessionCleanupAtom);
	const setDidRunCleanup = useSetAtom(didRunVidSessionCleanupAtom);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		if (!didRunCleanup) {
			setDidRunCleanup(true);
			purgeExpiredVidSessions(setDataStore);
		}
		if (!vidSessionId) {
			return;
		}
		// Starting or revisiting a session inside the TTL refreshes the expiry window.
		touchVidSessionUpdatedAt(vidSessionId);
	}, [didRunCleanup, setDidRunCleanup, setDataStore, vidSessionId]);
};

/**
 * @summary Reads vID session state by key with TTL cleanup.
 */
export function useSessionValue<K extends keyof VIDState>(key: K): VIDState[K] {
	const location = useLocation();
	const resolution = useVidStoragePartitionResolution();
	const partitionId = resolution.phase === "ok" ? resolution.partitionId : PENDING_STORAGE_KEY_RESOLUTION;

	useVidSessionCacheTtl(resolution.phase === "ok" ? resolution.partitionId : "");

	// Splitting this up into sessionAtom and keyAtom to correctly return the default state.
	// Apparently valueOr and prop don't work together on complex state.
	const sessionAtom = useMemo(() => {
		return focusAtom(dataStoreAtom, (optic) => optic.prop(partitionId).valueOr(defaultVIDStateValues));
	}, [partitionId]);

	const keyAtom = useMemo(() => {
		return focusAtom(sessionAtom, (optic) => optic.prop(key).valueOr(defaultVIDStateValues[key]));
	}, [sessionAtom, key]);

	const state = useAtomValue(keyAtom);

	if (resolution.phase === "missing") {
		if (true && isVidPathExemptFromPartitionGuard(location.pathname)) {
			return defaultVIDStateValues[key] as VIDState[K];
		}
		return throwMissingVidStorageKey();
	}

	return state as VIDState[K];
}

/**
 * @summary Writes vID session state by key and updates the session TTL.
 */
export function useSessionSet<K extends keyof VIDState>(
	key: K,
): (value: VIDState[K] | ((prev: VIDState[K]) => VIDState[K]) | typeof RESET) => void {
	const location = useLocation();
	const resolution = useVidStoragePartitionResolution();
	const partitionId = resolution.phase === "ok" ? resolution.partitionId : PENDING_STORAGE_KEY_RESOLUTION;

	useVidSessionCacheTtl(resolution.phase === "ok" ? resolution.partitionId : "");

	// [partitionId].[key] = value
	const sessionAtom = useMemo(() => {
		return focusAtom(dataStoreAtom, (optic) => optic.prop(partitionId).valueOr(defaultVIDStateValues).prop(key));
	}, [partitionId, key]);

	const setState = useSetAtom(sessionAtom);

	// Depend on primitive resolution fields: `useVidStoragePartitionResolution` returns a new
	// object every render, which would otherwise make this setter a new function each time.
	const resolutionPhase = resolution.phase;
	const resolvedPartitionId = resolution.phase === "ok" ? resolution.partitionId : "";

	const memoizedSetState = useCallback(
		(value: VIDState[K] | ((prev: VIDState[K]) => VIDState[K]) | typeof RESET) => {
			if (resolutionPhase !== "ok") {
				return;
			}
			touchVidSessionUpdatedAt(resolvedPartitionId);
			try {
				if (value === RESET) {
					setState(defaultVIDStateValues[key]);
				} else {
					setState((prev) => {
						// The first time this is called, prev will be undefined, since we haven't actually initialized the state yet.
						const confirmedPrev = prev ?? defaultVIDStateValues[key];
						return typeof value === "function" ? value(confirmedPrev) : value;
					});
				}
			} catch (error) {
				console.error(`Failed to save ${key} to localStorage:`, error);
				// Retry once
				try {
					if (value === RESET) {
						setState(defaultVIDStateValues[key]);
					} else {
						setState((prev) => {
							const confirmedPrev = prev ?? defaultVIDStateValues[key];
							return typeof value === "function" ? value(confirmedPrev) : value;
						});
					}
				} catch (retryError) {
					console.error(`Failed to save ${key} to localStorage on retry:`, retryError);
				}
			}
		},
		[resolutionPhase, resolvedPartitionId, setState, key],
	);

	if (resolution.phase === "missing") {
		if (true && isVidPathExemptFromPartitionGuard(location.pathname)) {
			return noopVidSessionSet<K>();
		}
		return throwMissingVidStorageKey();
	}

	return memoizedSetState;
}

/**
 * @summary Convenience hook returning vID session value and setter.
 */
export function useSession<K extends keyof VIDState>(
	key: K,
): [VIDState[K], (value: VIDState[K] | ((prev: VIDState[K]) => VIDState[K])) => void] {
	const value = useSessionValue<K>(key);
	const setValue = useSessionSet<K>(key);
	return [value, setValue];
}

// ------------------------

/**
 * @summary Reads and writes vID session file state backed by IndexedDB.
 */
export const useSessionFileAtom = (key: string, initialValue: File | null) => {
	const location = useLocation();
	const resolution = useVidStoragePartitionResolution();
	const partitionId = resolution.phase === "ok" ? resolution.partitionId : PENDING_STORAGE_KEY_RESOLUTION;

	useVidSessionCacheTtl(resolution.phase === "ok" ? resolution.partitionId : "");

	const fileAtom = useMemo(
		() => atomWithAsyncFileStorage(key, initialValue, () => partitionId),
		[key, initialValue, partitionId],
	);

	const value = useAtomValue(fileAtom);
	const setValue = useSetAtom(fileAtom);
	const resolutionPhase = resolution.phase;
	const resolvedPartitionId = resolution.phase === "ok" ? resolution.partitionId : "";
	const setValueWithTouch = useCallback(
		(value: File | typeof RESET) => {
			if (resolutionPhase !== "ok") {
				return;
			}
			touchVidSessionUpdatedAt(resolvedPartitionId);
			setValue(value);
		},
		[resolutionPhase, resolvedPartitionId, setValue],
	);

	if (resolution.phase === "missing") {
		if (true && isVidPathExemptFromPartitionGuard(location.pathname)) {
			return [initialValue, noopSessionFileSet] as const;
		}
		return throwMissingVidStorageKey();
	}

	return [value, setValueWithTouch] as const;
};
